import { Divider } from "@material-ui/core"
import React, { useContext } from "react"
import { AuthContext } from "../../../../context/Auth/AuthContext"

const MessageDivider = React.memo(
  /**
   * Componente visual responsável por inserir um divisor entre mensagens
   * quando há mudança no autor da mensagem (ex: de um usuário para outro).
   *
   * @component
   * @example
   * ```jsx
   * <MessageDivider
   *   message={msgAtual}
   *   previousMessage={msgAnterior}
   *   lEhConversaInterna={true}
   *   lUsuarioLogadoEhParticipante={false}
   * />
   * ```
   *
   * @param {{
   *  message: import("../MessageItem").Message,
   *  previousMessage?: import("../MessageItem").Message,
   *  classes: any,
   *  lEhConversaInterna?: boolean,
   *  lUsuarioLogadoEhParticipante?: boolean
   * }} props - Propriedades do componente.
   * @returns {JSX.Element | null} Retorna um `<Divider />` invisível ou `null`.
   */
  ({ message, previousMessage, classes, lEhConversaInterna, lUsuarioLogadoEhParticipante }) => {
    const { user } = useContext(AuthContext)
    if (!previousMessage) return null

    const nUsuarioLogadoId = user?.id

    // Para conversa interna onde usuário NÃO participa (visualizando como admin/supervisor)
    // Compara pelo usuarioId da mensagem para mostrar separação entre remetentes
    if (lEhConversaInterna && !lUsuarioLogadoEhParticipante) {
      // Se o remetente mudou, mostra divisor
      if (message.usuarioId !== previousMessage.usuarioId) {
        return <Divider style={{ margin: "16px 0 0 0", visibility: "hidden" }} />
      }
      return null
    }

    // Para conversa interna onde usuário participa
    // Compara se a mensagem é do usuário logado ou não
    if (lEhConversaInterna && lUsuarioLogadoEhParticipante) {
      const lMensagemAtualDoUsuario = message.usuarioId === nUsuarioLogadoId
      const lMensagemAnteriorDoUsuario = previousMessage.usuarioId === nUsuarioLogadoId

      // Só mostra o divisor se houve mudança de autor
      if (lMensagemAtualDoUsuario !== lMensagemAnteriorDoUsuario) {
        return <Divider style={{ margin: "16px 0 0 0", visibility: "hidden" }} />
      }
      return null
    }

    // Para conversas normais (não internas), usa fromMe
    if (message.fromMe !== previousMessage.fromMe) {
      return <Divider style={{ margin: "16px 0 0 0", visibility: "hidden" }} />
    }

    return null
  },
)

MessageDivider.displayName = "MessageDivider"

export default MessageDivider
